import { Injectable, OnDestroy } from "@angular/core";
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";

import Swal from "sweetalert2";

import { CoreMenuService } from "@core/components/core-menu/core-menu.service";
import UrlWithoutParamsHelper from "@core/helpers/url-without-params.helper";

import {
  AppPermissionsService,
  AuthenticationService,
  MenuAccessService,
} from "@common/services";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { MsalService } from "@azure/msal-angular";
import { Observable, Subject, of } from "rxjs";
import { catchError, filter, map, switchMap, take, takeUntil, timeout } from "rxjs/operators";

// Tiempo máximo que se espera a que la sesión termine de armarse. Solo aplica a la
// validación diferida, nunca a la navegación, así que puede ser holgado
const SESSION_WAIT_MS = 15000;

@Injectable({ providedIn: "root" })
export class AuthGuard  implements OnDestroy {

  private _unsubscribeAll: Subject<any>;

  /**
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   */
  constructor(
    private _router: Router,
    private _coreMenuService: CoreMenuService,
    private _authenticationService: AuthenticationService,
    private _permissionService: AppPermissionsService,
    private _menuAccessService: MenuAccessService,
    private _modalService: NgbModal,
    private _azureAuthService: MsalService
  ) {
    this._unsubscribeAll = new Subject();
  }


  /**
   * On Subscribe
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // canActivate
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
    // Concatena la url padre con la hija
    let url = route.data?.permissionUrl || UrlWithoutParamsHelper.getFullUrlWithoutParams(route);

    // Si la url inicia con slash se remueve porque se debe de buscar sin slash
    if (url.startsWith("/")) url = url.substring(1, url.length);

    // Al iniciar sesión o al recargar, la navegación ocurre antes de que exista el
    // token y antes de que estén los permisos, así que se espera a tenerlos. Solo
    // pasa mientras la sesión se está armando: una vez lista la validación es
    // inmediata y la navegación no se demora
    if (this.isSessionPending()) {
      return this.checkAccessWhenSessionReady(url, state);
    }

    return this.checkAccess(url, state);
  }

  /**
   * Indica si la sesión todavía se está armando, es decir si aún no hay un token
   * utilizable pero sí un inicio de sesión en camino.
   *
   * Cubre el primer inicio de sesión y la recarga de la aplicación: al volver del
   * proveedor de identidad no hay token guardado, y validar en ese momento cerraría
   * la sesión justo mientras se está obteniendo.
   */
  private isSessionPending(): boolean {
    // Con token, la sesión sigue armándose mientras los permisos no se hayan
    // resuelto. Es el caso de la autenticación local, donde el token se guarda
    // antes de pedir el usuario y sus permisos
    if (this._authenticationService.hasValidToken) {
      return !this._permissionService.hasPermissionsItems()
        && !this._permissionService.hasRegistered;
    }

    // La renovación ya arrancó
    if (this._authenticationService.isRenewingSession) return true;

    // Un token vencido sin renovación en curso no está por resolverse solo: la
    // renovación depende de que el usuario confirme la sesión vencida, así que se
    // valida de una en lugar de dejar la navegación esperando
    if (this._authenticationService.isAuthenticated()) return false;

    // Sin token todavía, pero hay una cuenta de Azure con la cual va a arrancar
    return this.hasAzureAccount();
  }

  /**
   * Indica si hay una cuenta activa en el proveedor de identidad
   */
  private hasAzureAccount(): boolean {
    try {
      return this._azureAuthService.instance.getAllAccounts().length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Comprueba el acceso a la url en cuanto la sesión esté lista, esperando primero
   * el token y después los permisos.
   *
   * La navegación queda pendiente de este resultado, así que la pantalla no llega a
   * mostrarse si al final no hay acceso. Si el usuario navega a otro lado mientras
   * tanto, el router cancela esta validación por su cuenta.
   *
   * @param url Url del menú a validar
   * @param state Estado del router de la navegación que originó la validación
   * @returns Devuelve si tiene acceso o no
   */
  private checkAccessWhenSessionReady(url: string, state: RouterStateSnapshot): Observable<boolean> {
    return this.waitForToken().pipe(
      switchMap(() => this.waitForPermissions()),
      map(() => this.checkAccess(url, state)),
      takeUntil(this._unsubscribeAll)
    );
  }

  /**
   * Espera a que exista un token utilizable
   */
  private waitForToken(): Observable<void> {
    if (this._authenticationService.hasValidToken) return of(undefined);

    return this._authenticationService.onSessionResolved.pipe(
      filter(() => this._authenticationService.hasValidToken),
      take(1),
      timeout(SESSION_WAIT_MS),
      catchError(() => of(undefined)),
      map(() => undefined)
    );
  }

  /**
   * Espera a que los permisos del usuario estén registrados en memoria
   */
  private waitForPermissions(): Observable<void> {
    if (this._permissionService.hasPermissionsItems()) return of(undefined);

    // Sin token no hay permisos que esperar: la validación redirige al login
    if (!this._authenticationService.isAuthenticated()) return of(undefined);

    // Se espera a que la carga quede resuelta, no a que haya permisos: un usuario
    // sin permisos nunca los tendría y la espera no terminaría nunca
    return this._permissionService.onPermissionsChanged.pipe(
      filter(() => this._permissionService.hasPermissionsItems()
        || this._permissionService.hasRegistered),
      take(1),
      timeout(SESSION_WAIT_MS),
      catchError(() => of(undefined)),
      map(() => undefined)
    );
  }

  /**
   * Evalúa si el usuario tiene acceso a la url y aplica la consecuencia: cerrar la
   * sesión o mandarlo al login.
   *
   * La usan los dos caminos del guard. Durante la navegación su resultado decide si
   * la ruta se activa; en la validación diferida la pantalla ya se está mostrando y
   * lo que importa son los efectos.
   *
   * @param url Url del menú a validar
   * @param state Estado del router de la navegación que originó la validación
   * @returns Devuelve si tiene acceso o no
   */
  private checkAccess(url: string, state: RouterStateSnapshot): boolean {
    // Si no hay sesión, o nunca llegó a armarse, se manda al login
    if (
      !this._authenticationService.isAuthenticated() ||
      this._authenticationService.isTokenExpired()
    ) {
      this._authenticationService.logout();
      // not logged in so redirect to login page with the return url
      this._router.navigate(["/pages/authentication/login"], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    // El superusuario tiene acceso global
    if (this._permissionService.isSuperUser) return true;

    // Los sitios incrustados se validan por el id del menú y no por la url: su
    // link guarda el sitio externo, así que no coincide con la ruta interna
    if (url === "embedded") {
      if (this.hasEmbeddedAccess(state.url)) return true;
      this.closeSession();
      return false;
    }

    // Si tiene cargado los permisos en memoria
    if (this._permissionService.hasPermissionsItems()) {
      // Verifica si tiene permisos para acceder a la url seleccionada
      if (!this._permissionService.hasPermissionByUrl(url, "READ")) {
        // Se cierra la sesión sino tiene accesos
        this.closeSession();
        return false;
      }
      return true;
    }

    // O Si no los tiene cargados en memoria, los busca por medio del api
    this.checkAccessByApi(url);

    // authorised so return true
    return true;
  }

  /**
   * Verifica contra el api si tiene acceso a la url y cierra la sesión sino lo tiene
   *
   * @param url Url del menú a validar
   */
  private checkAccessByApi(url: string): void {
    if (this._permissionService.isSuperUser) return;

    this._menuAccessService.haveAccess(url).pipe(takeUntil(this._unsubscribeAll)).subscribe(
      (response) => {
        // Sino tiene acceso a la url seleccionada
        if (!response) {
          // Se cierra la sesión sino tiene accesos
          this.closeSession();
        }
      },
      (error) => {
        if (error) {
          Swal.fire(
            "Oops...",
            "Existe problemas para comprobar accesos.",
            "error"
          );
        }
      }
    );
  }

  /**
   * Valida el acceso a un sitio incrustado (embedded/:id) por el id del menú.
   *
   * El id se saca de la url completa y no de los params de la ruta porque este
   * guard corre en la ruta padre, antes de que exista la ruta hija que declara
   * el :id.
   *
   * Si los permisos todavía no están en memoria se deja pasar: el componente
   * resuelve el sitio desde el menú del usuario, que ya viene filtrado por
   * permisos desde el backend, así que no puede incrustar algo ajeno.
   *
   * @param fullUrl Url completa del router (ej: /embedded/12)
   */
  private hasEmbeddedAccess(fullUrl: string): boolean {
    // Los sitios fijos (embedded/site/:key) no dependen de un menú: la url sale
    // de una lista del código y son accesos para cualquier usuario logueado
    if (/embedded\/site\//.test(fullUrl)) return true;

    if (!this._permissionService.hasPermissionsItems()) return true;

    const menuId = Number((fullUrl.match(/embedded\/(\d+)/) || [])[1]);
    return !!menuId && this._permissionService.hasPermissionByMenuId(menuId, "READ");
  }

  /**
   * Función que sirve para cerrar la sesión del usuario
   */
  private closeSession(): void {

    // Si tiene modales abiertos, los cierra todos
    if (this._modalService.hasOpenModals()){
      this._modalService.dismissAll();
    }

    // Redirige hacia la página donde no tiene autorización
    if (!this._authenticationService.isTokenExpired())
      this._router.navigate(["/pages/miscellaneous/not-authorized"]);
    // Redirige hacia la página donde la sesión ha expirado
    else this._router.navigate(["/pages/miscellaneous/session-expired"]);

    // Obtiene el menú cargado en memoria
    const menu = this._coreMenuService.getCurrentMenu();

    // Si el menú ya esta cargado en memoria
    if (!menu || menu.length == 0) {
      // Elimina de la memoria el menú
      this._coreMenuService.unregister("main");
      // Cierra la sesión
      this._authenticationService.logout();
    }
  }
}
